import companyJson from './company.json'
import { JsonReader } from './jsonReader'
import type { JsonObject } from './jsonReader'

// The operating company as registered in Brønnøysundregistrene (Enhetsregisteret).
export interface CompanyAddress {
  readonly lines: readonly string[]
  readonly postalCode: string
  readonly city: string
}

export interface Company {
  readonly legalName: string
  readonly orgNumber: string
  readonly vatRegistered: boolean
  readonly registryUrl: string
  readonly email: string
  readonly website: string
  readonly businessAddress: CompanyAddress
}

const COMPANY_SOURCE: string = 'src/data/company.json'
const ORG_NUMBER_PATTERN: RegExp = /^\d{9}$/
const POSTAL_CODE_PATTERN: RegExp = /^\d{4}$/
const WEBSITE_PATTERN: RegExp = /^https:\/\/[a-z0-9.-]+$/
// Enhetsregisteret's modulus-11 weights for the first eight digits; the ninth digit is the check digit.
const ORG_NUMBER_WEIGHTS: readonly number[] = [3, 2, 7, 6, 5, 4, 3, 2]
const MOD11: number = 11

function orgNumberCheckDigit(orgNumber: string): number | null {
  const sum: number = ORG_NUMBER_WEIGHTS.reduce((acc, weight, i) => acc + weight * Number(orgNumber[i]), 0)
  const remainder: number = sum % MOD11
  if (remainder === 0) return 0
  const digit: number = MOD11 - remainder
  return digit === 10 ? null : digit
}

function readOrgNumber(reader: JsonReader, value: unknown): string {
  const orgNumber: string = reader.string(value, 'orgNumber')
  if (!ORG_NUMBER_PATTERN.test(orgNumber)) reader.fail('orgNumber', `must be nine digits without spaces, got ${JSON.stringify(orgNumber)}`)
  if (orgNumberCheckDigit(orgNumber) !== Number(orgNumber[8])) reader.fail('orgNumber', `fails the Enhetsregisteret modulus-11 check digit, got ${JSON.stringify(orgNumber)}`)
  return orgNumber
}

function readAddress(reader: JsonReader, value: unknown, path: string): CompanyAddress {
  const raw: JsonObject = reader.object(value, path)
  const postalCode: string = reader.string(raw.postalCode, `${path}.postalCode`)
  if (!POSTAL_CODE_PATTERN.test(postalCode)) reader.fail(`${path}.postalCode`, `must be a four-digit Norwegian postal code, got ${JSON.stringify(postalCode)}`)
  const lines: readonly string[] = reader.array(raw.lines, `${path}.lines`).map((l, i) => reader.string(l, `${path}.lines[${i}]`))
  return { lines, postalCode, city: reader.string(raw.city, `${path}.city`) }
}

function readWebsite(reader: JsonReader, value: unknown): string {
  const website: string = reader.string(value, 'website')
  if (!WEBSITE_PATTERN.test(website)) reader.fail('website', `must be an https origin without a trailing slash, got ${JSON.stringify(website)}`)
  return website
}

function parseCompany(value: unknown): Company {
  const reader: JsonReader = new JsonReader(`company data ${COMPANY_SOURCE}`)
  const raw: JsonObject = reader.object(value, '(root)')
  const orgNumber: string = readOrgNumber(reader, raw.orgNumber)
  const registryUrl: string = reader.string(raw.registryUrl, 'registryUrl')
  if (!registryUrl.endsWith(`/${orgNumber}`)) reader.fail('registryUrl', `must point at org number ${orgNumber}, got ${JSON.stringify(registryUrl)}`)
  return {
    legalName: reader.string(raw.legalName, 'legalName'),
    orgNumber,
    vatRegistered: reader.boolean(raw.vatRegistered, 'vatRegistered'),
    registryUrl,
    email: reader.string(raw.email, 'email'),
    website: readWebsite(reader, raw.website),
    businessAddress: readAddress(reader, raw.businessAddress, 'businessAddress'),
  }
}

export const company: Company = parseCompany(companyJson)

/** "991851607" → "991 851 607", the grouping Enhetsregisteret uses. */
export function formatOrgNumber(orgNumber: string): string {
  return orgNumber.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3')
}

export function addressLines(address: CompanyAddress): readonly string[] {
  return [...address.lines, `${address.postalCode} ${address.city}`]
}
